'use client';

import { FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { type LayoutNodeStyleMap } from 'app/shop-ui/types';
import { combineComponent } from 'shop-ui/tools/combineComponent';
import { GRID_LAYOUT_TYPE } from 'shop-ui/section/Grid/rules';
import { OFFICIAL_FONT_SIZE_KEY, createRichText } from 'shop-ui/component/RichText';
import i18n from '@shared/i18n';

export enum OFFICIAL_SECTION_TEXT_KEY {
    DEFAULT = 'heading-section-default',
    VERTICAL = 'heading-subtitle-section-vertical',
    HORIZONTAL = 'heading-subtitle-section-horizontal',
}

export const officialSectionTextList = [
    {
        name: OFFICIAL_SECTION_TEXT_KEY.DEFAULT,
        content: i18n.gettext('Lorem ipsum dolor sit amet consectetur.'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_TEXT_KEY.VERTICAL,
        content: i18n.gettext('從這裡開始'),
        version: 1,
    },
    {
        name: OFFICIAL_SECTION_TEXT_KEY.HORIZONTAL,
        content: i18n.gettext('Adipiscing'),
        version: 1,
    },
];

export function createHeadingSectionDefault(
    content: string = officialSectionTextList[0].content,
    presetStyle: {
        heading?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
    } = {}
) {
    const { heading: headingPresetStyle = {}, primaryGrid: primaryGridPresetStyle = {} } =
        presetStyle;

    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.HEADING_SMALL, content, {
        [FEATURE_FLAG_KEYS.PADDING_LEFT]: '4%',
        [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
        [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '1px',
        [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '600',
        ...headingPresetStyle,
    });

    return combineComponent(i18n.gettext('標題區塊'), [heading], {
        primaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        primaryGridPresetStyle: {
            [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '428px',
            [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
            [FEATURE_FLAG_KEYS.GRID_GAP]: '10px',
            [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '36px',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '36px',
            ...primaryGridPresetStyle,
        },
        secondaryGridPresetStyle: [
            {
                [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
            },
        ],
        shouldWrap: true,
    });
}

export function createHeadingSubtitleSectionVertical(
    content: string = officialSectionTextList[1].content,
    paragraphContent: string = i18n.gettext(
        '這是一個全新的開始，這是一個文字輸入匡，你可以在這裡輸入你想要的文字'
    ),
    presetStyle: {
        heading?: LayoutNodeStyleMap[string];
        paragraph?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
    } = {}
) {
    const {
        heading: headingPresetStyle = {},
        paragraph: paragraphPresetStyle = {},
        primaryGrid: primaryGridPresetStyle = {},
    } = presetStyle;

    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '24',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(0, 0, 0, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '2',
        [FEATURE_FLAG_KEYS.PADDING_TOP]: '169px',
        ...headingPresetStyle,
    });

    const paragraph = createRichText(OFFICIAL_FONT_SIZE_KEY.CUSTOM, paragraphContent, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
        [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '13',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.4',
        [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '169px',
        ...paragraphPresetStyle,
    });

    return combineComponent(i18n.gettext('標題與內文 - 直'), [[heading, paragraph]], {
        primaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        primaryGridPresetStyle: {
            [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '100px',
            [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
            [FEATURE_FLAG_KEYS.GRID_GAP]: '10px',
            [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '36px',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '36px',
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '4%',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '4%',
            ...primaryGridPresetStyle,
        },
        secondaryGridPresetStyle: [
            {
                [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'center',
            },
        ],
        shouldWrap: true,
    });
}

export function createHeadingSubtitleSectionHorizontal(
    content: string = officialSectionTextList[2].content,
    presetStyle: {
        heading?: LayoutNodeStyleMap[string];
        paragraph?: LayoutNodeStyleMap[string];
        primaryGrid?: LayoutNodeStyleMap[string];
        paragraphContent?: string;
    } = {}
) {
    const {
        heading: headingPresetStyle = {},
        paragraph: paragraphPresetStyle = {},
        primaryGrid: primaryGridPresetStyle = {},
        paragraphContent = i18n.gettext(
            'Lorem ipsum dolor sit amet consectetur. Sed id sed gravida mauris sed. Platea fermentum turpis faucibus nisi magna morbi vel. Vitae eget dui quis vitae fringilla quisque elit blandit. Mi sagittis ipsum vel massa suspendisse lobortis orci enim tristique. Elit morbi commodo faucibus nulla. Nunc iaculis pellentesque dolor vulputate aenean. Aliquam placerat convallis sed vivamus tincidunt sed. Sodales sit leo libero pellentesque proin fermentum velit.'
        ),
    } = presetStyle;

    const heading = createRichText(OFFICIAL_FONT_SIZE_KEY.HEADING_MEDIUM, content, {
        [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'center',
        [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(0, 0, 0, 1)',
        [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '2',
        ...headingPresetStyle,
    });

    const paragraph = createRichText(
        OFFICIAL_FONT_SIZE_KEY.PARAGRAPH_MEDIUM,
        paragraphContent,
        {
            [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
            [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(124, 124, 128, 1)',
            [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.4',
            ...paragraphPresetStyle,
        }
    );

    return combineComponent(i18n.gettext('標題與內文 - 橫'), [heading, paragraph], {
        primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_2C,
        secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        primaryGridPresetStyle: {
            [FEATURE_FLAG_KEYS.MIN_HEIGHT]: '428px',
            [FEATURE_FLAG_KEYS.BACKGROUND_COLOR]: '',
            [FEATURE_FLAG_KEYS.GRID_GAP]: '112px',
            [FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT]: false,
            [FEATURE_FLAG_KEYS.PADDING_TOP]: '36px',
            [FEATURE_FLAG_KEYS.PADDING_BOTTOM]: '36px',
            [FEATURE_FLAG_KEYS.PADDING_LEFT]: '10%',
            [FEATURE_FLAG_KEYS.PADDING_RIGHT]: '10%',
            ...primaryGridPresetStyle,
        },
        secondaryGridPresetStyle: [
            {
                [FEATURE_FLAG_KEYS.GRID_ALIGNMENT]: 'start',
            },
        ],
        shouldWrap: true,
    });
}

export const officialSectionTextGenMap = {
    [OFFICIAL_SECTION_TEXT_KEY.DEFAULT]: createHeadingSectionDefault,
    [OFFICIAL_SECTION_TEXT_KEY.VERTICAL]: createHeadingSubtitleSectionVertical,
    [OFFICIAL_SECTION_TEXT_KEY.HORIZONTAL]: createHeadingSubtitleSectionHorizontal,
};
